import React, { useContext, useMemo, useState } from 'react';
import {
  Caption1, Caption1Strong, Spinner, makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';

import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';

import { Creative } from '../CreativeCard';
import { CreativesList } from './CreativesList';
import { NoUrlView } from './NoUrlView';
import { FilterControl } from './Filters';
import { CreativeFilterType } from './types';
import { CreativeFilterConfig } from './consts';

const useViewAllRecommendationsStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    paddingTop: tokens.spacingVerticalM,
    ...shorthands.overflow('hidden'),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  finalUrl: {
    ...shorthands.margin('4px'),
  },
  filters: {
    ...shorthands.margin('4px'),
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    flexGrow: 1,
    paddingTop: tokens.spacingVerticalS,
    overflowX: 'hidden',
  },
});

interface IViewAllRecommendationsProps {
  finalUrl: string;
  creatives: Creative[];
  isLoading: boolean;
  onFinalUrlEntered: (finalUrl: string) => void;
  onCreativeSelected: (creativeId: string) => void;
}

export const ViewAllRecommendations: React.FC<IViewAllRecommendationsProps> = ({
  finalUrl, creatives, isLoading, onFinalUrlEntered, onCreativeSelected,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useViewAllRecommendationsStyles();

  const [activeFilter, setActiveFilter] = useState<CreativeFilterType>('all');

  const filteredCreatives = useMemo(() => creatives.filter(CreativeFilterConfig[activeFilter].predicate), [creatives, activeFilter]);

  if (!finalUrl) {
    return (
      <div className={classes.root}>
        <NoUrlView onFinalUrlEntered={onFinalUrlEntered} />
      </div>
    );
  }

  return (
    <div className={mergeClasses('view-all-recommendations', classes.root)}>
      <div className={classes.finalUrl}>
        <Caption1Strong>{i18n.getString(_TL_('Final URL: '))}</Caption1Strong>
        <Caption1 className="final-url" title={finalUrl}>{finalUrl}</Caption1>
      </div>
      <div className={classes.filters}>
        <FilterControl activeFilter={activeFilter} onFilterChange={setActiveFilter} />
      </div>
      <div className={classes.content}>
        {!isLoading ? (
          <CreativesList
            creatives={filteredCreatives}
            selected={[]}
            onSelectedChange={(newSelected) => onCreativeSelected(newSelected[0])}
          />
        ) : (
          <Spinner aria-label={_TL_('Loading creative recommendations')} />
        )}
      </div>
    </div>
  );
};
